import { ResampleIntervals } from '@chart';
import { getTimeTillEndOfDayMs, isSinglePriceEntity } from '@helpers';
import {
  LAST_USED_CHART_PARAMS,
  readLastUsedUrl,
  removeLastUsedUrl,
} from '@terminal/hooks/use-persist-last-used-url/lastUsedUrlStorage';
import { queryClient } from '@terminal/services/query-client/queryClient';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { getFirstPopulatedWatchlist } from '@terminal/stores/use-watchlist/useWatchlist.utils';
import { appendChartAssetParam } from '@terminal/utils/chart-search-utils/chart-search-utils';
import {
  checkSearchParams,
  createAssetEntitiesWithSnake,
  getChartSettingsFromSearchParams,
  getInitialPrimaryAssetEntity,
  ValidChartSettings,
} from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';

import { getAssetResample } from '../../utils/resample-utils/resample-utils';
import {
  fetchChartEntities,
  fetchChartSnakeMeta,
} from '../use-chart-settings/useChartSettings.utils';
import { createChartTimeSeriesQueryOptions } from '../use-turbo-chart/useTurboChart.utils';

const DEFAULT_CHART_ENTITY_TAG = 'spy_arca';
const DEFAULT_CHART_SNAKE = 'spy_arca.price';

type LoadChartFromSettings = (settings: ValidChartSettings) => Promise<unknown>;
type ShouldLoadFallbackChart = () => boolean;

const getChartSettingsForEntity = (entityTag: string, snake: string) => {
  const searchParams = new URLSearchParams();
  appendChartAssetParam(searchParams, [entityTag, snake, 0]);

  return getChartSettingsFromSearchParams(searchParams);
};

const getStoredChartSettings = () => {
  const storedUrl = readLastUsedUrl(LAST_USED_CHART_PARAMS);

  if (!storedUrl) {
    return undefined;
  }

  try {
    const { searchParams } = new URL(storedUrl, window.location.origin);
    const settings = getChartSettingsFromSearchParams(searchParams);

    if (checkSearchParams(settings)) {
      return settings;
    }
  } catch {
    // no-op
  }

  removeLastUsedUrl(LAST_USED_CHART_PARAMS);
  return undefined;
};

const getFirstWatchlistChartSettings = async () => {
  const { error, lists } = useWatchlist.getState();

  if (error) {
    return undefined;
  }

  const firstPopulatedWatchlist = getFirstPopulatedWatchlist(lists);
  const firstEntity = firstPopulatedWatchlist?.entities.find(
    entity => entity.tag
  );

  if (!firstEntity) {
    return undefined;
  }

  if (firstEntity.asset?.default_snake) {
    return getChartSettingsForEntity(
      firstEntity.tag,
      firstEntity.asset.default_snake
    );
  }

  try {
    const [entity] = await fetchChartEntities({
      tags: [firstEntity.tag],
      queryClient,
    });

    return entity
      ? getChartSettingsForEntity(entity.tag, entity.default_snake)
      : undefined;
  } catch {
    return undefined;
  }
};

export const getFallbackChartSettings = async () =>
  getStoredChartSettings() ??
  (await getFirstWatchlistChartSettings()) ??
  getChartSettingsForEntity(DEFAULT_CHART_ENTITY_TAG, DEFAULT_CHART_SNAKE);

const tryLoadFallbackChart = async (
  settings: ValidChartSettings | undefined,
  loadChartFromSettings: LoadChartFromSettings,
  shouldLoad: ShouldLoadFallbackChart
) => {
  if (!settings || !shouldLoad()) {
    return false;
  }

  try {
    await loadChartFromSettings(settings);
    return true;
  } catch {
    return false;
  }
};

export const loadFallbackChart = async (
  loadChartFromSettings: LoadChartFromSettings,
  shouldLoad: ShouldLoadFallbackChart = () => true
) => {
  if (!shouldLoad()) {
    return false;
  }

  const storedSettings = getStoredChartSettings();

  if (
    await tryLoadFallbackChart(
      storedSettings,
      loadChartFromSettings,
      shouldLoad
    )
  ) {
    return true;
  }

  if (!shouldLoad()) {
    return false;
  }

  if (storedSettings) {
    removeLastUsedUrl(LAST_USED_CHART_PARAMS);
  }

  const watchlistSettings = await getFirstWatchlistChartSettings();

  if (
    await tryLoadFallbackChart(
      watchlistSettings,
      loadChartFromSettings,
      shouldLoad
    )
  ) {
    return true;
  }

  return tryLoadFallbackChart(
    getChartSettingsForEntity(DEFAULT_CHART_ENTITY_TAG, DEFAULT_CHART_SNAKE),
    loadChartFromSettings,
    shouldLoad
  );
};

export const prefetchFallbackChartData = async () => {
  try {
    const settings = await getFallbackChartSettings();
    const timeUntilEndOfDay = getTimeTillEndOfDayMs();
    const tags = settings.assets.map(asset => asset.tag);
    const [entities, snakeMetas] = await Promise.all([
      fetchChartEntities({
        tags,
        queryClient,
        gcTime: timeUntilEndOfDay,
      }),
      Promise.all(
        settings.assets.map(asset =>
          fetchChartSnakeMeta({
            snake: asset.snake,
            queryClient,
            gcTime: timeUntilEndOfDay,
          })
        )
      ),
    ]);
    const { assetEntities } = createAssetEntitiesWithSnake({
      assets: settings.assets,
      entities,
      snakeMetas,
    });

    if (!assetEntities.length) {
      return;
    }

    const { primaryAssetIndex } = getInitialPrimaryAssetEntity({
      assets: settings.assets,
      assetEntities,
      primarySnake: settings.primarySnake,
    });
    const primaryEntity = assetEntities[primaryAssetIndex];
    const hasSinglePriceEntity = assetEntities.some(isSinglePriceEntity);
    const resolution = getAssetResample({
      asset: primaryEntity,
      selectedResample: settings.resample,
      hasSinglePriceEntity,
    });
    const queries = assetEntities.map(asset =>
      queryClient.prefetchQuery(
        createChartTimeSeriesQueryOptions({
          latestAsset: asset,
          resolution,
          queryClient,
          staleTime: timeUntilEndOfDay,
          gcTime: timeUntilEndOfDay,
        })
      )
    );

    if (resolution !== ResampleIntervals.OneDay) {
      queries.push(
        queryClient.prefetchQuery(
          createChartTimeSeriesQueryOptions({
            latestAsset: primaryEntity,
            resolution: ResampleIntervals.OneDay,
            queryClient,
            staleTime: timeUntilEndOfDay,
            gcTime: timeUntilEndOfDay,
          })
        )
      );
    }

    await Promise.all(queries);
  } catch {
    return;
  }
};
